import { useEffect, useMemo, useState } from "react"
import {
  useGetBookInventoryDemandQuery,
  useLazyGetBookInventoryDemandQuery,
} from "shared/redux/endpoints/procurementItems"

// The demand endpoint is paged. Summary cards and client filters (status,
// window, search) have to see every center × book row, so pages after the
// first are accumulated. The numbers themselves are still the service's.
const PAGE_SIZE = 200
const EMPTY = []

const useBookDemand = ({ centerId, itemId } = {}) => {
  const args = {
    centerId: centerId || undefined,
    itemId: itemId || undefined,
  }
  const query = useGetBookInventoryDemandQuery({ ...args, page: 0, size: PAGE_SIZE })
  const [trigger] = useLazyGetBookInventoryDemandQuery()
  const [rest, setRest] = useState({ signature: "", items: [], error: false })

  const first = query.data?.items || EMPTY
  const total = query.data?.total ?? first.length
  const signature = `${args.centerId || ""}|${args.itemId || ""}|${total}|${first.length}|${query.fulfilledTimeStamp || 0}`
  const expectsMore = Boolean(query.data) && first.length < total
  const restMatches = rest.signature === signature
  const restItems = restMatches ? rest.items : EMPTY
  const restError = restMatches && rest.error
  const loadingRest = expectsMore && !restMatches

  useEffect(() => {
    if (!query.data || first.length >= total) return undefined
    let cancelled = false
    const pages = Math.ceil(total / PAGE_SIZE)
    const request = { centerId: centerId || undefined, itemId: itemId || undefined }
    Promise.all(
      Array.from({ length: pages - 1 }, (_, index) =>
        trigger({ ...request, page: index + 1, size: PAGE_SIZE }).unwrap()
      )
    )
      .then((pagesData) => {
        if (cancelled) return
        setRest({
          signature,
          items: pagesData.flatMap((page) => page?.items || []),
          error: false,
        })
      })
      .catch(() => {
        if (cancelled) return
        setRest({ signature, items: [], error: true })
      })
    return () => {
      cancelled = true
    }
  }, [query.data, first, total, centerId, itemId, signature, trigger])

  const rows = useMemo(
    () => (restError ? [] : [...first, ...restItems]),
    [restError, first, restItems]
  )

  return {
    rows,
    total,
    isLoading: (query.isFetching && !query.data) || loadingRest,
    isError: query.isError || restError,
    error: query.error,
    refetch: query.refetch,
  }
}

export default useBookDemand
